import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { STAGES } from "../../domain/stages.ts";
import type { BuilderApi } from "../api.ts";
import { Link, useNav, usePolling } from "../nav.tsx";
import { ErrorNote, STAGE_LABELS } from "../ui.tsx";

export function EmployeeList({ api }: { api: BuilderApi }) {
  const { go } = useNav();
  const [employees, , error] = usePolling(() => api.employees(), [], { ms: 10_000 });

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Employees</h1>
          <p className="mt-1 text-sm text-muted-foreground">Turn published studio avatars into employees tenants can hire.</p>
        </div>
        <Button onClick={() => go("/new")}>New employee</Button>
      </div>
      <ErrorNote error={error} />
      {!employees && !error && (
        <div className="grid gap-3 sm:grid-cols-2">
          <Skeleton className="h-36" />
          <Skeleton className="h-36" />
        </div>
      )}
      {employees?.length === 0 && (
        <Card>
          <CardHeader>
            <CardTitle>No employees yet</CardTitle>
            <CardDescription>Start from a published avatar. You will name it, give it knowledge, and let it interview you about the job.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => go("/new")}>Create the first employee</Button>
          </CardContent>
        </Card>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        {employees?.map((e) => {
          const done = STAGES.filter((s) => e.stages[s] === "done").length;
          const next = STAGES.find((s) => e.stages[s] === "open");
          return (
            <Link key={e.id} to={`/employees/${e.id}`} className="block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <Card className="h-full transition-colors hover:bg-accent/40">
                <CardHeader>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <CardTitle>{e.name}</CardTitle>
                      <CardDescription>{e.jobTitle || "No job title yet"}</CardDescription>
                    </div>
                    {e.latestVersion ? <Badge variant="success">v{e.latestVersion}</Badge> : <Badge variant="outline">Draft</Badge>}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-1" aria-label={`${done} of ${STAGES.length} stages done`}>
                    {STAGES.map((s) => (
                      <span
                        key={s}
                        title={`${STAGE_LABELS[s].title}: ${e.stages[s]}`}
                        className={`h-1.5 flex-1 rounded-full ${e.stages[s] === "done" ? "bg-success" : e.stages[s] === "open" ? "bg-info/50" : "bg-muted"}`}
                      />
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {done}/{STAGES.length} stages · {next ? `next: ${STAGE_LABELS[next].title}` : "all stages done"}
                    {e.avatarPin ? ` · avatar ${e.avatarPin.avatarName}` : ""}
                  </p>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
